import { getV2PluginAPIs } from "../plugins/plugins.svelte";
import { SafeDocument } from "../plugins/apiV3/v3.svelte";
import { getDatabase, normalizeChat } from "../storage/database.svelte";
import { DBState, selectedCharID, additionalSettingsMenu, additionalHamburgerMenu, LoadingStatusState, type MenuDef } from "../stores.svelte";
import { forageStorage } from "../globalApi.svelte";
import { NodeStorage } from "../storage/nodeStorage";
import { readPersistentJson, writePersistentJson, removePersistentKey, listPersistentKeys } from "../storage/persistentKv";
import { alertConfirm, alertError, alertNormal } from "../alert";
import { reportFeatureHealth } from "../featureHealth.svelte";
import { language } from "src/lang";
import { get } from "svelte/store";
import { v4 } from "uuid";

/*
    WygLore Leaf 네이티브 호스트

    플러그인 샌드박스(SandboxHost + postMessage RPC) 없이 WygLore Leaf 번들을
    same-origin iframe에서 구동하기 위한 호스트 백. iframe 전역에 `risuai`로
    주입되며, wyglore의 core/host.js가 V3 호스트로 인식한다.

    플러그인 API 대비 달라지는 점:
    - 권한 프롬프트 없음 (내장 기능이므로 항상 허용)
    - pluginStorage가 DB 블롭(pluginCustomStorage) 대신 persistentKv(서버 KV,
      `wyglore/` prefix)를 사용 — 읽기마다 전체 DB $state.snapshot을 뜨던
      OOM 원인이 제거되고, database.bin 크기에도 영향을 주지 않는다.
      해당 키는 Swift CDN 미러 대상 prefix가 아니므로 로컬 KV에 남는다(가변 데이터).
    - RPC 백로그로 인한 호스트 getter 행(hang) 자체가 불가능 (직접 함수 호출)
*/

const WL_KV_PREFIX = "wyglore/kv/";
const WL_ARG_PREFIX = "wyglore/arg/";
const WL_ROOT_PREFIX = "wyglore/";

/*
    P2 그래프 row 스토어 (WYGLORE-P2-DESIGN.md).

    큰 그래프 스냅샷을 "전량 재직렬화 → 샤딩 블롭"에서 "유닛-단위 증분 델타 →
    서버 SQLite row"로 옮기는 클라이언트 노출부. vendor 엔진(registry.js)이
    host.wygloreGraphStore capability 를 감지하면 이 경로를, 없으면 기존 블롭
    경로를 쓴다 (node 테스트/플러그인 환경은 capability 부재 → 무회귀).

    엔드포인트(server.cjs, checkAuth 필수):
      - GET    /api/wyglore/graph/:graphId        → NDJSON 스트림 로드
      - POST   /api/wyglore/graph/:graphId/flush  → 증분 저장(단일 트랜잭션)
      - DELETE /api/wyglore/graph/:graphId        → 그래프 전체 삭제(wipe)

    임베딩은 Float32Array ↔ base64(원시 LE 바이트). vendor 의 encode/decodeEmbedding
    과 동일 바이트 규약이라 라운드트립이 해시-안정적이다.
*/

/** base64(원시 바이트) → Float32Array. 4의 배수 아니면 null. */
function wlBase64ToFloat32(b64: string | null | undefined): Float32Array | null {
    if (typeof b64 !== "string" || !b64) return null;
    try {
        const bin = atob(b64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        if ((bytes.length & 3) !== 0) return null;
        return new Float32Array(bytes.buffer, bytes.byteOffset, bytes.length >> 2);
    } catch {
        return null;
    }
}

/** Float32Array → base64(원시 LE 바이트). vendor encodeEmbedding 과 동일 규약. */
function wlFloat32ToBase64(f32: Float32Array | null | undefined): string | null {
    if (!(f32 instanceof Float32Array)) return null;
    const bytes = new Uint8Array(f32.buffer, f32.byteOffset, f32.byteLength);
    let bin = "";
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
        const slice = bytes.subarray(i, Math.min(i + CHUNK, bytes.length));
        bin += String.fromCharCode.apply(null, slice as unknown as number[]);
    }
    try {
        return btoa(bin);
    } catch {
        return null;
    }
}

/** graphId(= registry webKey) → same-origin 그래프 엔드포인트 경로. */
function wlGraphUrl(graphId: string, suffix = ""): string {
    return `/api/wyglore/graph/${encodeURIComponent(graphId)}${suffix}`;
}

/** nodeStorage.authFetch 와 동일한 risu-auth + x-session-id 헤더 구성 (same-origin).
    x-session-id 를 보내야 서버의 단일-writer 가드(423)가 그래프 쓰기에도 걸린다 —
    없으면 두 탭이 같은 그래프를 동시에 flush 해 lost-update 가 날 수 있다. */
async function wlAuthHeaders(extra?: Record<string, string>): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
        "risu-auth": await forageStorage.createAuth(),
        "x-session-id": NodeStorage.getSessionId(),
    };
    if (extra) Object.assign(headers, extra);
    return headers;
}

/*
    연결 테스트용 비파괴 서버 프로브 (QuickSetup 진단에서 호출).
    존재하지 않는 graphId GET 은 서버(loadGraph)가 200 + 빈 스냅샷을 돌려주므로
    라우트·인증·SQLite 경로 전체를 데이터 변경 없이 검증한다.
    상태는 featureHealth 로도 보고해 상태등이 함께 갱신된다.
*/
export async function wygloreServerProbe(): Promise<{ ok: boolean; detail: string }> {
    try {
        const res = await fetch(wlGraphUrl("__healthcheck__"), {
            method: "GET",
            headers: await wlAuthHeaders(),
        });
        if (res.status >= 200 && res.status < 300) {
            reportFeatureHealth("wyglore", "ok");
            return { ok: true, detail: `HTTP ${res.status}` };
        }
        if (res.status === 423) {
            // 다른 탭이 세션 점유 — 서버 자체는 살아있는 정상 멀티탭 상태
            return { ok: false, detail: "session held by another tab (423)" };
        }
        reportFeatureHealth("wyglore", "error", `probe HTTP ${res.status}`);
        return { ok: false, detail: `HTTP ${res.status}` };
    } catch (e: any) {
        const detail = String(e?.message ?? e);
        reportFeatureHealth("wyglore", "error", `probe failed: ${detail}`);
        return { ok: false, detail };
    }
}

/** 내장 iframe 기동 여부 (연결 테스트의 클라이언트측 절반). */
export function wygloreIframeActive(): boolean {
    return !!document.querySelector("iframe[data-wyglore-native]");
}

/*
    P1 벌크 하이드레이트 (WYGLORE-PERF-PLAN.md):
    부트 시 `wyglore/` prefix 전체를 /api/assets/bulk-read 1회(+list 1회)로 적재해
    이후 getItem을 동기 메모리 조회로 만든다. 쓰기는 캐시 갱신 + persistentKv
    write-through. 하이드레이트 실패 시 키 단위 지연 로드로 자동 폴백.
*/
const kvCache = new Map<string, string>(); // 전체 storage 키 → 문자열 값
let hydrated = false;

export async function hydrateWygloreStorage(): Promise<void> {
    try {
        // 부트에서 가장 긴 구간 — 로딩 화면에 항목 수를 보고해 생존 신호를 준다.
        LoadingStatusState.text = ((language as any).bootWygloreList ?? "Listing memory data...");
        const keys = await listPersistentKeys(WL_ROOT_PREFIX);
        if (keys.length > 0) {
            const tmpl: string = (language as any).bootWygloreHydrate ?? "Loading memory data ({{count}} items)...";
            LoadingStatusState.text = tmpl.replace("{{count}}", String(keys.length));
            const items = await forageStorage.getItems(keys);
            const decoder = new TextDecoder();
            for (const { key, value } of items) {
                try {
                    const parsed = JSON.parse(decoder.decode(value));
                    if (typeof parsed === "string") kvCache.set(key, parsed);
                } catch (e) { /* 개별 항목 손상 — 지연 로드 경로에 맡김 */ }
            }
        }
        hydrated = true;
        console.log(`[WygLore native] storage hydrated: ${kvCache.size}/${keys.length} keys`);
    } catch (e) {
        console.warn("[WygLore native] bulk hydrate 실패 — 키 단위 지연 로드로 폴백", e);
    }
}

async function cachedGet(fullKey: string): Promise<string | null> {
    if (kvCache.has(fullKey)) return kvCache.get(fullKey)!;
    if (hydrated) return null; // 하이드레이트 완료 후 미존재 키 — HTTP 왕복 생략
    const v = await readPersistentJson<string>(fullKey);
    if (typeof v === "string") kvCache.set(fullKey, v);
    return v === undefined || v === null ? null : v;
}

async function cachedSet(fullKey: string, value: string): Promise<void> {
    kvCache.set(fullKey, value);
    await writePersistentJson(fullKey, value);
}

async function cachedRemove(fullKey: string): Promise<void> {
    kvCache.delete(fullKey);
    await removePersistentKey(fullKey);
}

const unloadCallbacks: Function[] = [];

export function runWygloreUnloadCallbacks() {
    for (const cb of unloadCallbacks.splice(0)) {
        try { cb(); } catch (e) { console.error("[WygLore native] unload callback failed", e); }
    }
}

function makeMenuUnregister(menuId: string, menuStore: MenuDef[]) {
    return () => {
        const index = menuStore.findIndex(item => item.id === menuId);
        if (index !== -1) menuStore.splice(index, 1);
    };
}

let toastContainer: HTMLDivElement | null = null;
function nativeToast(message: string, durationMs = 6000) {
    try {
        if (!toastContainer || !toastContainer.isConnected) {
            toastContainer = document.createElement("div");
            toastContainer.style.cssText =
                "position:fixed;bottom:16px;left:50%;transform:translateX(-50%);z-index:2147483000;" +
                "display:flex;flex-direction:column;gap:8px;align-items:center;pointer-events:none;max-width:min(92vw,560px);";
            document.body.appendChild(toastContainer);
        }
        const el = document.createElement("div");
        el.textContent = String(message ?? "");
        el.style.cssText =
            "background:rgba(24,24,27,0.92);color:#fafafa;padding:10px 16px;border-radius:10px;" +
            "font-size:13px;line-height:1.5;box-shadow:0 4px 16px rgba(0,0,0,0.35);pointer-events:auto;" +
            "border:1px solid rgba(255,255,255,0.08);word-break:keep-all;overflow-wrap:break-word;";
        toastContainer.appendChild(el);
        setTimeout(() => {
            el.style.transition = "opacity 0.4s";
            el.style.opacity = "0";
            setTimeout(() => el.remove(), 450);
        }, durationMs);
    } catch (e) {
        console.log("[WygLore native toast]", message);
    }
}

export function wygloreNativeToast(message: string) {
    nativeToast(message, 8000);
}

export function buildWygloreNativeHost(iframe: HTMLIFrameElement) {
    const oldApis = getV2PluginAPIs();

    // 회상·주입(beforeRequest) 성패를 상태등에 연결. 성공 시 ok 하트비트 —
    // 부트 시점 값으로 동결되던 상태를 매 턴 갱신한다. 탈출 예외는 보고 후
    // 그대로 재던져 엔진의 의도(slow-abort fail-closed)를 보존하되,
    // SlowAbort 는 새 턴 진입에 의한 정상 취소일 수 있어 장애로 보고하지 않는다.
    // 엔진 내부에서 삼켜진(fail-open) 오류는 여기서 보이지 않는다 — 알려진 한계.
    //
    // **하트비트는 WygLore 가 실제로 처리하는 요청에만** 울린다. vendor 는
    // `if (type && type !== "model") return messages` 로 메인 채팅(model)/undefined
    // 만 처리하고 submodel·translate·emotion·memory 는 즉시 건너뛴다(index.js:549).
    // 하트비트를 type 무관하게 울리면 자동번역·자동추천 등 모든 LLM 호출에서
    // WygLore 가 "발동"한 것처럼 상태가 갱신되는 회귀가 생긴다 — vendor 게이트와
    // 동일 조건으로 맞춘다.
    const wygloreProcesses = (type: unknown) => !(type && type !== "model");
    const replacerWrapMap = new WeakMap<Function, any>();
    const wrapBeforeRequest = (func: (...args: any[]) => any) => {
        const wrapped = async (...args: any[]) => {
            const type = args[1];
            if (!wygloreProcesses(type)) {
                // WygLore 가 건드리지 않는 보조 호출 — 그대로 통과, 상태 보고 없음.
                return func(...args);
            }
            try {
                const r = await func(...args);
                reportFeatureHealth("wyglore", "ok");
                return r;
            } catch (e: any) {
                if (!(e && (e.wlSlowAbort === true || e.name === "SlowAbortError"))) {
                    reportFeatureHealth("wyglore", "error",
                        `beforeRequest threw: ${String(e?.message ?? e).slice(0, 200)}`);
                }
                throw e;
            }
        };
        replacerWrapMap.set(func, wrapped);
        return wrapped;
    };

    return {
        // --- 요청 파이프라인 (핵심 훅) ---
        addRisuReplacer: (type: string, func: (...args: any[]) => any) => {
            if (type !== "beforeRequest") return oldApis.addRisuReplacer(type as any, func);
            return oldApis.addRisuReplacer(type as any, wrapBeforeRequest(func));
        },
        removeRisuReplacer: (type: string, func: (...args: any[]) => any) => {
            // 래핑으로 함수 정체성이 바뀌므로 원본→래퍼 매핑으로 해제한다.
            const wrapped = replacerWrapMap.get(func) ?? func;
            return oldApis.removeRisuReplacer(type as any, wrapped);
        },
        addRisuScriptHandler: oldApis.addRisuScriptHandler,
        removeRisuScriptHandler: oldApis.removeRisuScriptHandler,
        addProvider: oldApis.addProvider,

        // --- 네트워크 ---
        nativeFetch: oldApis.nativeFetch,
        risuFetch: oldApis.risuFetch,

        // --- 세션/캐릭터 (v3.svelte.ts 구현과 동일 시맨틱, 권한 게이트만 제거) ---
        getCharacter: oldApis.getChar,
        setCharacter: oldApis.setChar,
        getChar: oldApis.getChar,
        setChar: oldApis.setChar,
        getCurrentCharacterIndex: () => {
            return get(selectedCharID);
        },
        getCurrentChatIndex: () => {
            const db = DBState.db;
            const charId = get(selectedCharID);
            return db.characters[charId]?.chatPage;
        },
        getChatFromIndex: (characterIndex: number, chatIndex: number) => {
            const db = DBState.db;
            const charIds = Object.keys(db.characters);
            const charId = charIds[characterIndex];
            if (charId) {
                const chats = db.characters[charId].chats;
                if (chats && chats[chatIndex]) {
                    return $state.snapshot(chats[chatIndex]);
                }
            }
            return null;
        },
        setChatToIndex: (characterIndex: number, chatIndex: number, chat: any) => {
            const db = DBState.db;
            const charIds = Object.keys(db.characters);
            const charId = charIds[characterIndex];
            if (charId) {
                const chats = db.characters[charId].chats;
                if (chats && chats[chatIndex]) {
                    DBState.db.characters[charId].chats[chatIndex] = normalizeChat(chat);
                }
            }
        },
        getCharacterFromIndex: (index: number) => {
            const db = DBState.db;
            const charIds = Object.keys(db.characters);
            const charId = charIds[index];
            if (charId) {
                return $state.snapshot(db.characters[charId]);
            }
            return null;
        },
        setCharacterToIndex: (index: number, char: any) => {
            const db = DBState.db;
            const charIds = Object.keys(db.characters);
            const charId = charIds[index];
            if (charId) {
                DBState.db.characters[charId] = char;
            }
        },

        // --- 저장소: persistentKv 기반 (DB 블롭 밖, 전체 스냅샷 없음) + P1 하이드레이트 캐시 ---
        pluginStorage: {
            getItem: async (key: string): Promise<string | null> => {
                if (typeof key !== "string" || !key) return null;
                return await cachedGet(WL_KV_PREFIX + key);
            },
            setItem: async (key: string, value: string): Promise<void> => {
                if (typeof key !== "string" || !key) return;
                await cachedSet(WL_KV_PREFIX + key, value);
            },
            removeItem: async (key: string): Promise<void> => {
                if (typeof key !== "string" || !key) return;
                await cachedRemove(WL_KV_PREFIX + key);
            },
            keys: async (): Promise<string[]> => {
                if (hydrated) {
                    return [...kvCache.keys()]
                        .filter(k => k.startsWith(WL_KV_PREFIX))
                        .map(k => k.slice(WL_KV_PREFIX.length));
                }
                const keys = await listPersistentKeys(WL_KV_PREFIX);
                return keys.map(k => k.slice(WL_KV_PREFIX.length));
            },
        },
        getArgument: async (key: string) => {
            if (typeof key !== "string" || !key) return undefined;
            const v = await cachedGet(WL_ARG_PREFIX + key);
            return v === null ? undefined : v;
        },
        setArgument: async (key: string, value: string) => {
            if (typeof key !== "string" || !key) return;
            await cachedSet(WL_ARG_PREFIX + key, value);
        },

        // --- UI 등록 (스토어 직접 push — v3.svelte.ts 구현 미러) ---
        registerSetting: (
            name: string,
            callback: any,
            icon: string = "",
            iconType: "html" | "img" | "none" = "none",
            id?: string
        ) => {
            const menuId = id || v4();
            const menuDef: MenuDef = { id: menuId, name, icon, iconType, callback };
            const existingIndex = additionalSettingsMenu.findIndex(item => item.id === menuId);
            if (existingIndex !== -1) {
                additionalSettingsMenu[existingIndex] = menuDef;
            } else {
                additionalSettingsMenu.push(menuDef);
            }
            unloadCallbacks.push(makeMenuUnregister(menuId, additionalSettingsMenu));
            return { id: menuId };
        },
        registerButton: (
            arg: { name: string; icon: string; iconType: "html" | "img" | "none"; location?: "action" | "chat" | "hamburger"; id?: string },
            callback: () => void
        ) => {
            const id = arg.id || v4();
            const menuDef: MenuDef = { name: arg.name, icon: arg.icon, iconType: arg.iconType, callback, id };
            // wyglore는 hamburger만 사용한다; 다른 위치가 필요해지면 v3 구현을 따라 확장
            const existingIndex = additionalHamburgerMenu.findIndex(item => item.id === id);
            if (existingIndex !== -1) {
                additionalHamburgerMenu[existingIndex] = menuDef;
            } else {
                additionalHamburgerMenu.push(menuDef);
            }
            unloadCallbacks.push(makeMenuUnregister(id, additionalHamburgerMenu));
            return { id };
        },
        unregisterUIPart: (id: string) => {
            for (const store of [additionalSettingsMenu, additionalHamburgerMenu]) {
                const index = store.findIndex(item => item.id === id);
                if (index !== -1) store.splice(index, 1);
            }
        },

        // --- 컨테이너 (wyglore 자체 iframe 풀스크린 토글) ---
        showContainer: (type: "fullscreen" = "fullscreen") => {
            iframe.style.display = "block";
            if (iframe.parentElement !== document.body) {
                document.body.appendChild(iframe);
            }
            iframe.style.position = "fixed";
            iframe.style.top = "0";
            iframe.style.left = "0";
            iframe.style.width = "100%";
            iframe.style.height = "100%";
            iframe.style.border = "none";
            iframe.style.zIndex = "1000";
        },
        hideContainer: () => {
            iframe.style.display = "none";
        },
        // 권한 프롬프트 없이 메인 문서 접근 (SafeElement API 시맨틱은 실호스트와 동일)
        getRootDocument: () => {
            return new SafeDocument(document);
        },

        // --- 라이프사이클/기타 ---
        onUnload: (callback: () => void) => {
            unloadCallbacks.push(callback);
        },
        getRuntimeInfo: () => {
            return {
                apiVersion: "3.0",
                platform: "node",
                saveMethod: forageStorage.isAccount ? "account" : "local",
                native: true,
            };
        },
        log: (...args: any[]) => {
            console.log("[WygLore native]", ...args);
        },
        toast: (message: string) => {
            nativeToast(message);
        },
        alert: (msg: string) => alertNormal(msg),
        alertConfirm: (msg: string) => alertConfirm(msg),
        alertError: (msg: string) => alertError(msg),
        requestPluginPermission: async () => true,

        // --- P2 그래프 row 스토어 (capability). registry.js 가 이 존재를 감지해 델타 경로 선택 ---
        wygloreGraphStore: {
            /*
                GET NDJSON 스트림을 한 줄씩 파싱해 { meta, units } 반환.
                거대 단일 JSON.parse 를 만들지 않는다 — ReadableStream reader +
                라인 버퍼링으로 유닛 객체를 점진 구성한다. base64 임베딩 → Float32Array.
            */
            load: async (graphId: string): Promise<{ meta: any; units: Array<{ id: string; json: any; embedding: Float32Array | null }> }> => {
                const res = await fetch(wlGraphUrl(graphId), {
                    method: "GET",
                    headers: await wlAuthHeaders({ accept: "application/x-ndjson" }),
                });
                if (res.status === 423) {
                    window.dispatchEvent(new CustomEvent("risu-session-deactivated"));
                }
                if (res.status < 200 || res.status >= 300) {
                    // 423(다른 탭 세션 점유)은 정상 멀티탭 상태 — 장애 보고 제외
                    if (res.status !== 423) reportFeatureHealth("wyglore", "error", `graph load HTTP ${res.status}`);
                    throw new Error(`wygloreGraphStore.load ${res.status}`);
                }
                const units: Array<{ id: string; json: any; embedding: Float32Array | null }> = [];
                let meta: any = null;
                const decoder = new TextDecoder();
                let buffer = "";
                const handleLine = (line: string) => {
                    if (!line) return;
                    let msg: any;
                    try { msg = JSON.parse(line); } catch { return; }
                    if (msg.type === "meta") {
                        meta = msg.meta ?? null;
                    } else if (msg.type === "unit" && typeof msg.id === "string") {
                        units.push({
                            id: msg.id,
                            json: msg.json,
                            embedding: wlBase64ToFloat32(msg.embedding),
                        });
                    }
                };
                if (res.body && typeof res.body.getReader === "function") {
                    const reader = res.body.getReader();
                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) break;
                        buffer += decoder.decode(value, { stream: true });
                        const lines = buffer.split("\n");
                        buffer = lines.pop() ?? "";
                        for (const line of lines) handleLine(line);
                    }
                    buffer += decoder.decode();
                } else {
                    // 스트리밍 미지원 환경(테스트/폴리필) — 텍스트 일괄 폴백.
                    buffer += await res.text();
                }
                for (const line of buffer.split("\n")) handleLine(line);
                return { meta, units };
            },

            /*
                증분 저장. upserts 의 Float32Array 임베딩을 base64 로 인코딩해 전송.
                서버는 단일 트랜잭션으로 upsert(INSERT OR REPLACE)+delete+meta 갱신.
            */
            flush: async (
                graphId: string,
                delta: { meta: any; upserts: Array<{ id: string; json: any; embedding: Float32Array | null }>; deletes: string[] },
            ): Promise<{ ok: boolean; unitCount: number }> => {
                const upserts = (delta.upserts || []).map((u) => ({
                    id: u.id,
                    json: u.json,
                    embedding: wlFloat32ToBase64(u.embedding),
                }));
                const body = JSON.stringify({
                    meta: delta.meta ?? null,
                    upserts,
                    deletes: delta.deletes || [],
                });
                const res = await fetch(wlGraphUrl(graphId, "/flush"), {
                    method: "POST",
                    headers: await wlAuthHeaders({ "content-type": "application/json" }),
                    body,
                });
                if (res.status === 423) {
                    window.dispatchEvent(new CustomEvent("risu-session-deactivated"));
                }
                if (res.status < 200 || res.status >= 300) {
                    if (res.status !== 423) reportFeatureHealth("wyglore", "error", `graph flush HTTP ${res.status}`);
                    throw new Error(`wygloreGraphStore.flush ${res.status}`);
                }
                // 저장이 실제로 통과했다 = 런타임 정상 신호(오류→정상 복귀 반영)
                reportFeatureHealth("wyglore", "ok");
                const data = await res.json();
                return { ok: data?.ok !== false, unitCount: data?.unitCount | 0 };
            },

            /* 세션 wipe — 해당 graph_id 전체 삭제. */
            wipe: async (graphId: string): Promise<void> => {
                const res = await fetch(wlGraphUrl(graphId), {
                    method: "DELETE",
                    headers: await wlAuthHeaders(),
                });
                if (res.status < 200 || res.status >= 300) {
                    if (res.status !== 423) reportFeatureHealth("wyglore", "error", `graph wipe HTTP ${res.status}`);
                    throw new Error(`wygloreGraphStore.wipe ${res.status}`);
                }
            },
        },

        // 내부 마커
        __wlNative: true,
    };
}
